import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { AdminCard, AdminHeader, Field } from "./AdminShell";

type ProductFormState = {
  title: string; slug: string; subtitle: string; description: string; detail_html: string; sku: string;
  price: number; compare_at_price?: number; cost_price?: number; stock: number; status: string;
  main_image: string; gallery_images: string; is_featured: boolean; is_new: boolean; is_hot: boolean;
  seo_title: string; seo_description: string; category_id?: number;
};

const initial: ProductFormState = { title: "", slug: "", subtitle: "", description: "", detail_html: "", sku: "", price: 0, stock: 0, status: "draft", main_image: "", gallery_images: "", is_featured: false, is_new: false, is_hot: false, seo_title: "", seo_description: "" };

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState<ProductFormState>(initial);
  const [message, setMessage] = useState("");
  const gallery = form.gallery_images.split(/\n|,|\|/).map((url) => url.trim()).filter(Boolean);

  useEffect(() => {
    if (id) api<any>(`/api/admin/products/${id}`).then((p) => setForm({ ...initial, ...p, gallery_images: p.gallery_images || "" })).catch(() => undefined);
  }, [id]);

  function set<K extends keyof ProductFormState>(key: K, value: ProductFormState[K]) { setForm((prev) => ({ ...prev, [key]: value })); }

  async function save() {
    const path = id ? `/api/admin/products/${id}` : "/api/admin/products";
    await api(path, { method: id ? "PUT" : "POST", body: JSON.stringify(form) });
    setMessage("Product saved.");
    if (!id) navigate("/admin/products");
  }

  return (
    <div>
      <AdminHeader title={id ? "Edit Product" : "Add Product"} description="Products use one title and one description. Storefront interface language changes independently from product content." />
      {message ? <div className="mb-5 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">{message}</div> : null}
      <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
        <AdminCard title="Product Content">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Product Title"><input className="input" value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
            <Field label="Slug"><input className="input" value={form.slug} onChange={(e) => set("slug", e.target.value)} /></Field>
            <Field label="Subtitle"><input className="input" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} /></Field>
            <Field label="SKU"><input className="input" value={form.sku} onChange={(e) => set("sku", e.target.value)} /></Field>
            <div className="md:col-span-2"><Field label="Description"><textarea className="input min-h-28 rounded-[24px]" value={form.description} onChange={(e) => set("description", e.target.value)} /></Field></div>
            <div className="md:col-span-2"><Field label="Product Detail HTML"><textarea className="input min-h-36 rounded-[24px] font-mono" value={form.detail_html} onChange={(e) => set("detail_html", e.target.value)} /></Field></div>
          </div>
        </AdminCard>
        <AdminCard title="Images">
          <div className="space-y-4">
            <Field label="Main Image URL"><input className="input" value={form.main_image} onChange={(e) => set("main_image", e.target.value)} /></Field>
            <img src={form.main_image || "/placeholder.webp"} className="aspect-[4/3] w-full rounded-[24px] object-cover bg-[#FDF9F1]" />
            <Field label="Gallery Image URLs"><textarea className="input min-h-32 rounded-[24px]" placeholder="One URL per line, or comma separated" value={form.gallery_images} onChange={(e) => set("gallery_images", e.target.value)} /></Field>
            <div className="grid grid-cols-3 gap-2">{gallery.map((url) => <img key={url} src={url} className="aspect-square rounded-2xl object-cover" />)}</div>
          </div>
        </AdminCard>
        <AdminCard title="Pricing & Inventory">
          <div className="grid gap-4 md:grid-cols-4">
            <Field label="Price"><input className="input" type="number" value={form.price} onChange={(e) => set("price", Number(e.target.value))} /></Field>
            <Field label="Compare Price"><input className="input" type="number" value={form.compare_at_price || ""} onChange={(e) => set("compare_at_price", Number(e.target.value))} /></Field>
            <Field label="Cost Price"><input className="input" type="number" value={form.cost_price || ""} onChange={(e) => set("cost_price", Number(e.target.value))} /></Field>
            <Field label="Stock"><input className="input" type="number" value={form.stock} onChange={(e) => set("stock", Number(e.target.value))} /></Field>
          </div>
        </AdminCard>
        <AdminCard title="Publishing">
          <div className="space-y-4">
            <Field label="Status"><select className="input" value={form.status} onChange={(e) => set("status", e.target.value)}><option>draft</option><option>active</option><option>archived</option></select></Field>
            {(["is_featured", "is_new", "is_hot"] as const).map((key) => <label key={key} className="flex items-center gap-3 text-sm font-bold"><input type="checkbox" checked={!!form[key]} onChange={(e) => set(key, e.target.checked)} /> {key.replace("is_", "Mark as ")}</label>)}
          </div>
        </AdminCard>
        <AdminCard title="SEO" className="xl:col-span-2">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="SEO Title"><input className="input" value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></Field>
            <Field label="SEO Description"><input className="input" value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} /></Field>
          </div>
          <button className="btn-primary mt-6" onClick={save}><Save size={16} className="mr-2" />Save Product</button>
        </AdminCard>
      </div>
    </div>
  );
}
